/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Close, Save } from "@mui/icons-material";
import {
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import { Form, FormikProvider, useFormik } from "formik";
import * as yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";

import PageContainer from "@jumbo/components/PageContainer";
import LoadingButton from "@jumbo/components/JumboLoadingButton";
import { MILL_PURCHASE } from "app/routes/constant";
import {
  createMillPurchase,
  getByIdMillPurchase,
  updateMillPurchase,
} from "app/redux/slice/millPurchaseSlice";
import JumboAutoComplete from "@jumbo/components/JumboAutoComplete";
import { getOrderNo } from "app/redux/slice/invoiceSlice";
import { getProductList } from "app/redux/slice/productSlice";
import { getSupplierList } from "app/redux/slice/supplierSlice";
import { getByIdKnitterOrder } from "app/redux/slice/knitterOrderSlice";
import { istDateInput, istToday } from "app/helpers/istDate";

const useStyles = makeStyles({
  headingContainer: {
    display: "flex",
    justifyContent: "center",
    marginBottom: "20px",
  },
  mainInfoContainer: {
    display: "flex",
    minHeight: "170px",
    justifyContent: "center",
    flexDirection: "column",
  },
  headingTitleContainer: {
    display: "flex",
    flexGrow: "wrap",
  },
  headingTitle: {
    minWidth: "80px",
  },
  headingContent: {
    fontWeight: "500",
  },
  qrContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  table: {
    minWidth: 650,
    "& .MuiTableCell-root": {
      borderLeft: "1px solid",
      borderBottom: "1px solid",
    },
  },
  particular: {
    minWidth: "300px",
  },
  variants: {
    minWidth: "300px",
  },
  amount: {
    minWidth: "100px",
    verticalAlign: "top",
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "end",
    margin: "10px 0px",
  },
  margin: {
    "& > :first-child": {
      marginTop: "10px",
    },
    marginBottom: "10px",
  },
  alignTop: {
    verticalAlign: "top",
  },
  loadingButtonContainer: {
    display: "flex",
    justifyContent: "end",
    marginTop: "20px",
  },
});

const validationSchema = yup.object({
  purchaseNumber: yup.string().required("Please enter PO number."),
  purchaseDate: yup.string().required("Please enter PO date."),
  supplierId: yup.object().required("Please select vendor").nullable(),
  productId: yup.object().required("Please select product").nullable(),
  quantity: yup
    .number()
    .typeError("Quantity must be a number")
    .required("Please enter number of role")
    .min(1),
  unitPrice: yup
    .number()
    .typeError("Unit price must be a number")
    .required("Please enter rate per KG")
    .min(1),
  lotNumber: yup.number().typeError("Lot number must be a number").min(1),
});

const AddAndUpdatePurchase = () => {
  const { id, knitterOrderId } = useParams();
  const navigate = useNavigate();
  const classes = useStyles();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const supplierData = useSelector((state) => state?.supplier);
  const productData = useSelector((state) => state?.product);
  const [isUpdate, setIsUpdate] = useState(false);
  const mobileView = useMediaQuery("(max-width:575px)");

  const formik = useFormik({
    initialValues: {
      purchaseNumber: "",
      purchaseDate: istToday(),
      supplierId: null,
      productId: null,
      quantity: "",
      unitPrice: "",
      totalAmount: "",
    },
    validationSchema,
    validateOnChange: false,
    onSubmit: async (values, { resetForm, setFieldError, setSubmitting }) => {
      setSubmitting(true);
      const newPurchase = {
        ...(knitterOrderId && { knitterOrderId }),
        purchaseNumber: values.purchaseNumber,
        purchaseDate: values.purchaseDate,
        supplierId: values?.supplierId?._id,
        productId: values?.productId?._id,
        quantity: values?.quantity,
        unitPrice: values?.unitPrice,
        totalAmount: values?.totalAmount,
        lotNumber: values?.lotNumber,
      };
      const response = await dispatch(
        id
          ? updateMillPurchase({ id, data: newPurchase })
          : createMillPurchase(newPurchase),
      ).unwrap();
      if (response.status === 200) {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "success",
        });
        navigate(MILL_PURCHASE);
        resetForm();
      } else {
        enqueueSnackbar(response.data?.responseMessage, {
          variant: "error",
        });
      }
      setSubmitting(false);
    },
  });

  const { values, setFieldValue, errors } = formik;

  useEffect(() => {
    if (id) {
      dispatch(getByIdMillPurchase(id))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const millPurchaseDetail = response?.data?.data?.millPurchaseDetail;
            setFieldValue("supplierId", millPurchaseDetail?.supplierId);
            setFieldValue("productId", millPurchaseDetail?.productId);
            setFieldValue("purchaseNumber", millPurchaseDetail?.purchaseNumber);
            setFieldValue(
              "purchaseDate",
              istDateInput(millPurchaseDetail?.purchaseDate),
            );
            setFieldValue("quantity", millPurchaseDetail?.quantity);
            setFieldValue("unitPrice", millPurchaseDetail?.unitPrice);
            setFieldValue("lotNumber", millPurchaseDetail?.lotNumber);
            setIsUpdate(millPurchaseDetail.remainingQty > 0 ? true : false);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
    if (knitterOrderId) {
      dispatch(getByIdKnitterOrder(knitterOrderId))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const knitterOrderList = response?.data?.data?.knitterOrderList;
            // setFieldValue("supplierId", knitterOrderList?.supplierId);
            setFieldValue("productId", knitterOrderList?.productId);
            setFieldValue(
              "purchaseDate",
              istDateInput(knitterOrderList?.purchaseDate),
            );
            setFieldValue("quantity", knitterOrderList?.remainingQty);
            setFieldValue("unitPrice", knitterOrderList?.unitPrice);
            setFieldValue("lotNumber", knitterOrderList?.lotNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
  }, [dispatch, enqueueSnackbar, id, setFieldValue]);

  useEffect(() => {
    if (!id || knitterOrderId) {
      dispatch(getOrderNo({ type: "millPurchase" }))
        .unwrap()
        .then((response) => {
          if (response.status === 200) {
            const newOrderNumber = response?.data?.data?.orderNo;
            setFieldValue("purchaseNumber", newOrderNumber);
          } else {
            enqueueSnackbar(response.data?.responseMessage, {
              variant: "error",
            });
          }
        });
    }
    dispatch(getProductList({ field: "name", sort: 1 }));
    dispatch(getSupplierList({ sort: 1, field: "name", type: "Mill" }));
  }, []);

  const { handleSubmit } = formik;
  return (
    <PageContainer heading={id ? "Update Purchase" : "Add Purchase"}>
      <Card>
        <CardContent>
          <FormikProvider value={formik}>
            <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
              <Grid container border={1}>
                <Grid item sm={4} xs={12} borderRight={{ xs: 0, sm: 1 }}>
                  <Grid>
                    <Grid px={1} pt={1} mb={1}>
                      <JumboAutoComplete
                        options={supplierData.list ?? []}
                        label="Vendor*"
                        size="small"
                        getOptionLabel={(option) => option?.name}
                        name={"supplierId"}
                        value={values?.supplierId}
                        onChange={(evt, value) => {
                          setFieldValue("supplierId", value);
                        }}
                        helperText={formik?.errors?.supplierId}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item sm={8} xs={12}>
                  <Grid container>
                    <Grid
                      item
                      sm={6}
                      xs={12}
                      borderRight={{ xs: 0, sm: 1 }}
                      p={1}
                    >
                      <TextField
                        label={"Purchase No."}
                        name="purchaseNumber"
                        fullWidth
                        size="small"
                        value={values?.purchaseNumber}
                        onChange={(evt) =>
                          setFieldValue("purchaseNumber", evt.target.value)
                        }
                        error={errors?.purchaseNumber}
                        helperText={errors?.purchaseNumber}
                      />
                    </Grid>
                    <Grid item sm={6} xs={12} p={1}>
                      <TextField
                        label={"Date"}
                        name="purchaseDate"
                        fullWidth
                        size="small"
                        type="date"
                        onChange={(evt) =>
                          setFieldValue("purchaseDate", evt.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                        value={values?.purchaseDate}
                        error={errors?.purchaseDate}
                        helperText={errors?.purchaseDate}
                      />
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
              <Grid
                item
                xs={12}
                borderRight={1}
                sx={{
                  mt: { xs: 3, md: 0 },
                }}
                className="mobile-view-table"
              >
                <TableContainer component={Paper}>
                  <Table
                    sx={{ minWidth: 650 }}
                    size="small"
                    aria-label="a dense table"
                    className={classes?.table}
                  >
                    <TableHead>
                      <TableRow>
                        {!mobileView ? <TableCell>Sr. No.</TableCell> : ""}
                        <TableCell className={classes?.particular}>
                          Product
                        </TableCell>
                        <TableCell>Qty</TableCell>
                        <TableCell>Unit Price</TableCell>
                        <TableCell>lot Number</TableCell>
                        <TableCell className={classes?.amount}>
                          Amount
                        </TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow>
                        {!mobileView ? (
                          <TableCell sx={{ width: "10px" }}>1</TableCell>
                        ) : (
                          ""
                        )}
                        <TableCell className={classes?.particular}>
                          <JumboAutoComplete
                            options={productData.list ?? []}
                            label="Product*"
                            size="small"
                            getOptionLabel={(option) => option?.name}
                            name={`productId`}
                            value={values?.productId}
                            onChange={(evt, value) => {
                              setFieldValue("productId", value);
                            }}
                            helperText={formik?.errors?.productId}
                          />
                        </TableCell>

                        <TableCell className={classes?.alignTop}>
                          <TextField
                            placeholder="Num Of Roles"
                            name={`quantity`}
                            fullWidth
                            type="text"
                            value={values?.quantity}
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("quantity", +evt.target.value)
                            }
                            error={errors?.quantity}
                            helperText={errors?.quantity}
                          />
                        </TableCell>
                        <TableCell className={classes?.alignTop}>
                          <TextField
                            placeholder="Rate Per Kg"
                            name={`unitPrice`}
                            fullWidth
                            type="text"
                            value={values?.unitPrice}
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("unitPrice", +evt.target.value)
                            }
                            error={errors?.unitPrice}
                            helperText={errors?.unitPrice}
                          />
                        </TableCell>
                        <TableCell className={classes?.alignTop}>
                          <TextField
                            placeholder="lot Number"
                            name={`lotNumber`}
                            fullWidth
                            type="text"
                            value={values?.lotNumber}
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("lotNumber", +evt.target.value ?? 0)
                            }
                            error={errors?.lotNumber}
                            helperText={errors?.lotNumber}
                          />
                        </TableCell>
                        <TableCell className={classes?.amount}>
                          <TextField
                            placeholder="Rate Per Kg"
                            name={`totalAmount`}
                            fullWidth
                            type="text"
                            value={
                              values.quantity && values.unitPrice
                                ? values.quantity * values?.unitPrice
                                : 0
                            }
                            size="small"
                            onChange={(evt) =>
                              setFieldValue("totalAmount", +evt.target.value)
                            }
                            error={errors?.totalAmount}
                            helperText={errors?.totalAmount}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
              <Grid container spacing={3}>
                <Grid
                  item
                  sm={12}
                  xs={12}
                  className={classes?.loadingButtonContainer}
                >
                  <LoadingButton
                    disabled={id && !isUpdate}
                    type="submit"
                    buttonLabel={id ? "Update" : "Save"}
                    sx={{ marginRight: "10px" }}
                    startIcon={<Save />}
                    variant="outlined"
                    fullWidth={mobileView}
                  />
                  <LoadingButton
                    color={"secondary"}
                    buttonLabel={"Close"}
                    variant="outlined"
                    startIcon={<Close />}
                    onClick={() => navigate(MILL_PURCHASE)}
                    fullWidth={mobileView}
                  />
                </Grid>
              </Grid>
            </Form>
          </FormikProvider>
        </CardContent>
      </Card>
    </PageContainer>
  );
};

export default AddAndUpdatePurchase;
